// NEW_FILE_JUSTIFIED: Replaces stub; VoiceAgentsListPage is the primary agents dashboard page
import { useState, useCallback, useMemo } from 'react';
import { useOrgContext } from '@/hooks/useOrgContext';
import {
  useVoiceAgentsList,
  useVoiceAgentMutations,
  useVoicePhoneNumbers,
  useVoiceSipTrunks,
} from '@/hooks/useVoiceAgents';
import { useVoiceDashboardStats } from '@/hooks/useVoiceDashboardStats';
import { VoiceDashboardStats } from '@/components/voiceAgents/VoiceDashboardStats';
import { VoiceAgentCard } from '@/components/voiceAgents/VoiceAgentCard';
import { CreateAgentDialog } from '@/components/voiceAgents/CreateAgentDialog';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Bot } from 'lucide-react';
import type {
  ICreateVoiceAgentFormData,
  IVoiceAgent,
  IVoicePhoneNumber,
} from '@/@types/voiceAgents';
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/ require three levels up

export function VoiceAgentsListPage() {
  const { currentOrg, isLoading: orgLoading } = useOrgContext();

  // null = closed, 'create' = new agent, Id = editing that agent
  const [dialogMode, setDialogMode] = useState<'create' | Id<'voiceAgents'> | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Id<'voiceAgents'> | null>(null);

  const { data: agents } = useVoiceAgentsList(currentOrg?._id);
  const { stats } = useVoiceDashboardStats(currentOrg?._id);
  const { data: phoneNumbers } = useVoicePhoneNumbers(currentOrg?._id);
  const { data: sipTrunks } = useVoiceSipTrunks(currentOrg?._id);

  const { create, update, toggleActive, remove } = useVoiceAgentMutations();

  const phoneNumberMap = useMemo(
    () => new Map((phoneNumbers ?? []).map((p: IVoicePhoneNumber) => [p._id, p] as const)),
    [phoneNumbers]
  );

  const editingAgent = useMemo(() => {
    if (!dialogMode || dialogMode === 'create') return undefined;
    return (agents ?? []).find((a: IVoiceAgent) => a._id === dialogMode);
  }, [dialogMode, agents]);

  const handleSubmitAgent = useCallback(
    async (data: ICreateVoiceAgentFormData) => {
      if (!currentOrg) return;
      if (dialogMode && dialogMode !== 'create' && editingAgent) {
        await update({ agentId: editingAgent._id, ...data });
      } else {
        await create({ ...data, orgId: currentOrg._id });
      }
      setDialogMode(null);
    },
    [currentOrg, create, update, dialogMode, editingAgent]
  );

  const handleToggleActive = useCallback(
    (agentId: Id<'voiceAgents'>, _isActive: boolean) => {
      void toggleActive({ agentId });
    },
    [toggleActive]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    await remove({ agentId: deleteTarget });
    setDeleteTarget(null);
  }, [deleteTarget, remove]);

  if (!orgLoading && !currentOrg) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <Bot className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">No organisation found</h3>
          <p className="text-sm text-muted-foreground">
            You need to be part of an organisation to manage voice agents.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background-secondary py-8 px-10 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Voice Agents</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Manage AI voice agents, monitor calls, and track costs
          </p>
        </div>
        <Button onClick={() => setDialogMode('create')}>
          <Plus aria-hidden="true" className="w-4 h-4 mr-2" />
          Create Agent
        </Button>
      </div>

      {/* Dashboard Stats */}
      <VoiceDashboardStats stats={stats} />

      {/* Agent Cards */}
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-4">
          Agents {agents ? `(${agents.length})` : ''}
        </h2>
        {agents === undefined && (
          <p className="text-sm text-muted-foreground py-8 text-center">Loading agents...</p>
        )}
        {agents?.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Bot className="w-10 h-10 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">No agents created yet</p>
          </div>
        )}
        {agents && agents.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {agents.map((agent: IVoiceAgent) => (
              <VoiceAgentCard
                key={agent._id}
                agent={agent}
                phoneNumber={
                  agent.phoneNumberId
                    ? (phoneNumberMap.get(agent.phoneNumberId) as IVoicePhoneNumber | undefined) // AS_JUSTIFIED: Map.get returns unknown due to const tuple inference; value is always IVoicePhoneNumber
                    : undefined
                }
                onEdit={agentId => setDialogMode(agentId)}
                onToggleActive={handleToggleActive}
                onDelete={setDeleteTarget}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Agent Dialog */}
      <CreateAgentDialog
        open={dialogMode !== null}
        onOpenChange={open => {
          if (!open) setDialogMode(null);
        }}
        onSubmit={data => void handleSubmitAgent(data)}
        isSubmitting={false}
        phoneNumbers={phoneNumbers ?? []}
        sipTrunks={sipTrunks ?? []}
        title={editingAgent ? 'Edit Voice Agent' : 'Create Voice Agent'}
        initialData={
          editingAgent
            ? {
                name: editingAgent.name,
                type: editingAgent.type,
                systemPrompt: editingAgent.systemPrompt,
                allowedTools: editingAgent.allowedTools,
                llmModel: editingAgent.llmModel,
                llmFallback: editingAgent.llmFallback,
                sttProvider: editingAgent.sttProvider,
                sttFallback: editingAgent.sttFallback,
                ttsProvider: editingAgent.ttsProvider,
                ttsFallback: editingAgent.ttsFallback,
                voiceId: editingAgent.voiceId,
                phoneNumberId: editingAgent.phoneNumberId,
                requireConsent: editingAgent.requireConsent,
                consentMessage: editingAgent.consentMessage,
                verificationRequired: editingAgent.verificationRequired,
                verificationMethod: editingAgent.verificationMethod,
                preemptiveGeneration: editingAgent.preemptiveGeneration,
                turnDetectionSensitivity: editingAgent.turnDetectionSensitivity,
                maxToolCallsPerCall: editingAgent.maxToolCallsPerCall,
                maxCallDurationMs: editingAgent.maxCallDurationMs,
                maxDeniedBeforeTransfer: editingAgent.maxDeniedBeforeTransfer,
                maxReplySentences: editingAgent.maxReplySentences,
                humanEscalationEnabled: editingAgent.humanEscalationEnabled,
                escalationPhoneNumber: editingAgent.escalationPhoneNumber,
                escalationSipTrunkId: editingAgent.escalationSipTrunkId,
                costRates: editingAgent.costRates,
              }
            : undefined
        }
      />

      {/* Delete Confirmation */}
      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={open => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Voice Agent?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the agent. Call history will be preserved but no new
              calls can be handled.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleConfirmDelete()}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
